import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import {
  AppstoreOutlined,
  BellOutlined,
  CalendarOutlined,
  LineChartOutlined,
  GiftOutlined,
  FileSearchOutlined,
  SettingOutlined,
  ShopOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { history } from '@umijs/max';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
import {
  currentAdmin,
  getStoredAdmin,
  getToken,
  isTokenAlive,
  type NoraAdmin,
} from '@/services/nora/auth';
import { ADMIN_API_BASE_URL } from '../config/runtimeEnv';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const loginPath = '/user/login';

function toCurrentUser(admin?: NoraAdmin): API.CurrentUser | undefined {
  if (!admin) return undefined;
  const realName = admin.realName || admin.real_name || admin.username;
  return {
    userid: String(admin.id),
    name: realName,
    email: admin.email,
    access: admin.role,
    username: admin.username,
    realName,
    role: admin.role,
    permissions: admin.permissions || [],
    menuPermissions: admin.menuPermissions || [],
  };
}

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    const token = getToken();
    if (!isTokenAlive(token)) return undefined;

    try {
      const admin = await currentAdmin();
      return toCurrentUser(admin);
    } catch {
      return toCurrentUser(getStoredAdmin());
    }
  };

  const { location } = history;
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo();
    if (!currentUser) {
      history.replace(loginPath);
    }
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

const menuKeyByPath: Record<string, string> = {
  '/users': 'menu:users',
  '/posts': 'menu:posts',
  '/official-actions': 'menu:official-actions',
  '/operations/checkin-tips': 'menu:checkin-tips',
  '/operations/refresh-tips': 'menu:refresh-tips',
  '/operations/splash': 'menu:splash',
  '/operations/app-sections': 'menu:app-sections',
  '/operations/ip-library': 'menu:ip-library',
  '/operations/mini-program': 'menu:mini-program',
  '/operations/official-notices': 'menu:official-notices',
  '/operations/tabbar-icons': 'menu:tabbar-icons',
  '/operations/avatar-frames': 'menu:avatar-frames',
  '/operations/stickers': 'menu:stickers',
  '/operations/activities': 'menu:activities',
  '/operations/recommend-banners': 'menu:recommend-banners',
  '/operations/feedback': 'menu:feedback',
  '/operations/points': 'menu:points',
  '/operations/point-exchanges': 'menu:point-exchanges',
  '/market/shops': 'menu:market-review',
  '/market/items': 'menu:market-review',
  '/market/sale': 'menu:market-review',
  '/market/orders': 'menu:market-review',
  '/market/categories': 'menu:market-review',
  '/commissions/review': 'menu:commission-review',
  '/commissions/orders': 'menu:commission-orders',
  '/commissions/disputes': 'menu:commission-orders',
  '/commissions/withdrawals': 'menu:commission-wallet',
  '/commissions/wallets': 'menu:commission-wallet',
  '/conventions': 'menu:conventions',
  '/convention-organizer-applications': 'menu:conventions',
  '/analytics/stats': 'menu:stats',
  '/stats': 'menu:stats',
  '/analytics': 'menu:analytics',
  '/system/config': 'menu:config',
  '/system/push': 'menu:config',
  '/system/admins': 'menu:admins',
  '/system/logs': 'menu:logs',
};

function hasMenuAccess(currentUser: API.CurrentUser | undefined, path?: string) {
  if (!path || path === '/dashboard') return true;
  if (currentUser?.role === 'super_admin') return true;

  const matchedKey = Object.entries(menuKeyByPath).find(([prefix]) => path.startsWith(prefix))?.[1];
  if (!matchedKey) return true;

  return currentUser?.menuPermissions?.includes(matchedKey);
}

function filterMenus(items: any[], currentUser: API.CurrentUser | undefined): any[] {
  return items
    .map((item) => {
      const children = item.children ? filterMenus(item.children, currentUser) : undefined;
      const selfVisible = hasMenuAccess(currentUser, item.path);
      if (!selfVisible && (!children || children.length === 0)) return null;
      return { ...item, children };
    })
    .filter(Boolean);
}

function cloneMenuItem(item: any): any {
  if (!item) return null;
  return {
    ...item,
    children: Array.isArray(item.children)
      ? item.children.map(cloneMenuItem).filter(Boolean)
      : undefined,
  };
}

function collectMenuByPath(items: any[], target: Map<string, any>) {
  items.forEach((item) => {
    if (item?.path && !target.has(item.path)) {
      target.set(item.path, item);
    }
    if (Array.isArray(item?.children)) {
      collectMenuByPath(item.children, target);
    }
  });
}

function menuGroup(key: string, name: string, icon: any, children: any[]) {
  const visibleChildren = children.filter(Boolean);
  if (!visibleChildren.length) return null;
  return {
    key,
    name,
    icon,
    locale: false,
    children: visibleChildren,
  };
}

function buildGroupedMenus(menuData: any[], currentUser: API.CurrentUser | undefined): any[] {
  const filteredMenus = filterMenus(menuData, currentUser);
  const menuByPath = new Map<string, any>();
  const usedTopLevelPaths = new Set(['/operations']);
  collectMenuByPath(filteredMenus, menuByPath);

  const take = (path: string) => {
    const item = menuByPath.get(path);
    if (!item) return null;
    const topLevel = filteredMenus.find((menu) => menu.path === path);
    if (topLevel?.path) {
      usedTopLevelPaths.add(topLevel.path);
    }
    return cloneMenuItem(item);
  };

  const takeChildren = (path: string) => {
    const item = menuByPath.get(path);
    if (!item) return [];
    usedTopLevelPaths.add(path);
    return (item.children || []).filter((child: any) => child?.name).map(cloneMenuItem);
  };

  const dashboard = take('/dashboard');
  const groupedMenus = [
    dashboard,
    menuGroup('menu-group-community', '用户与内容', <TeamOutlined />, [
      take('/users'),
      take('/posts'),
      take('/official-actions'),
    ]),
    menuGroup('menu-group-app', 'App配置', <AppstoreOutlined />, [
      take('/operations/splash'),
      take('/operations/app-sections'),
      take('/operations/ip-library'),
      take('/operations/mini-program'),
      take('/operations/tabbar-icons'),
      take('/operations/recommend-banners'),
    ]),
    menuGroup('menu-group-engagement', '激励互动', <GiftOutlined />, [
      take('/operations/points'),
      take('/operations/point-exchanges'),
      take('/operations/avatar-frames'),
      take('/operations/stickers'),
      take('/operations/activities'),
      take('/operations/checkin-tips'),
      take('/operations/refresh-tips'),
    ]),
    menuGroup('menu-group-notice', '通知反馈', <BellOutlined />, [
      take('/operations/official-notices'),
      take('/operations/feedback'),
    ]),
    menuGroup('menu-group-market', '交易小铺', <ShopOutlined />, takeChildren('/market')),
    menuGroup('menu-group-commission', '委托管理', <FileSearchOutlined />, takeChildren('/commissions')),
    menuGroup('menu-group-convention', '漫展票务', <CalendarOutlined />, takeChildren('/conventions')),
    menuGroup('menu-group-analytics', '数据分析', <LineChartOutlined />, takeChildren('/analytics')),
    menuGroup('menu-group-system', '系统权限', <SettingOutlined />, takeChildren('/system')),
  ].filter(Boolean);

  const passthroughMenus = filteredMenus.filter((item) => {
    if (!item?.path || item.path === '*' || !item.name) return false;
    return !usedTopLevelPaths.has(item.path);
  });

  return [...groupedMenus, ...passthroughMenus.map(cloneMenuItem)];
}

export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    menuDataRender: (menuData) => buildGroupedMenus(menuData, initialState?.currentUser),
    onPageChange: () => {
      const { location } = history;
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.replace(loginPath);
      }
    },
    menuHeaderRender: undefined,
    ...initialState?.settings,
  };
};

export const request: RequestConfig = {
  baseURL: ADMIN_API_BASE_URL,
  ...errorConfig,
};
